import React, { useEffect, useRef, useState } from 'react';
import './AddressManagement.css';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store/store';
import { useApiServices } from '../../services/endpoints/ApiEndpoint';
import Button from '../../components/button/Button';
import { useAlert } from '../../components/alert-context/AlertContext';

interface Address {
    id: string;
    name: string;
    addressLine1: string;
    addressLine2: string;
    latitude: number;
    longitude: number;
    postalCode: string;
    country: string;
    isDefault: boolean;
    createDate: string;
    contact: {
        id: string;
        name: string;
        phone: string;
    };
}

type Mode = 'list' | 'form';

const emptyForm = {
    name: '',
    phone: '',
    addressLine1: '',
    addressLine2: '',
    postalCode: '',
};

const AddressManagement: React.FC = () => {
    const navigate = useNavigate();
    const alert = useAlert();
    const api = useApiServices();
    const user = useSelector((state: RootState) => state.user);

    const [mode, setMode] = useState<Mode>('list');
    const [addresses, setAddresses] = useState<Address[]>([]);
    const [editingAddress, setEditingAddress] = useState<Address | null>(null);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);

    const apiRef = useRef(api);
    apiRef.current = api;

    const fetchAddresses = useRef(async () => {
        try {
            const { isSuccess, result } = await apiRef.current.GetAddressesAsync(user?.entityUniqueIdentifierId);
            if (isSuccess && Array.isArray(result)) {
                const sorted = result.sort(
                    (a: any, b: any) => new Date(b.createDate).getTime() - new Date(a.createDate).getTime()
                );
                setAddresses(sorted);
            }
        } catch {
            // silent
        }
    });

    useEffect(() => {
        if (user?.entityUniqueIdentifierId) fetchAddresses.current();
    }, [user?.entityUniqueIdentifierId]);

    const openEdit = (address: Address) => {
        setEditingAddress(address);
        setForm({
            name: address.contact?.name || '',
            phone: address.contact?.phone || '',
            addressLine1: address.addressLine1,
            addressLine2: address.addressLine2 || '',
            postalCode: address.postalCode,
        });
        setMode('form');
    };

    const handleSave = async () => {
        if (!form.name || !form.phone || !form.addressLine1 || !form.postalCode) {
            alert.error({ message: 'กรุณากรอกข้อมูลให้ครบถ้วน' });
            return;
        }
        if (!editingAddress) return;

        setSaving(true);
        try {
            const payload = {
                id: editingAddress.id,
                contact: { name: form.name, phone: form.phone },
                name: form.addressLine1,
                addressLine1: form.addressLine1,
                addressLine2: form.addressLine2,
                postalCode: form.postalCode,
                country: editingAddress.country,
                latitude: editingAddress.latitude,
                longitude: editingAddress.longitude,
                isDeleted: false,
            };
            const response = await api.UpdateAddressesAsync(user?.entityUniqueIdentifierId, payload);
            if (response.isSuccess) {
                alert.success({ message: 'แก้ไขที่อยู่สำเร็จ' });
                setMode('list');
                fetchAddresses.current();
            } else {
                alert.error({ message: response.message || 'ไม่สามารถแก้ไขที่อยู่ได้' });
            }
        } catch {
            alert.error({ message: 'เกิดข้อผิดพลาดในการแก้ไขที่อยู่' });
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = (address: Address) => {
        alert.confirm({
            message: `ต้องการลบที่อยู่ "${address.addressLine1}" หรือไม่?`,
            confirmText: 'ลบ',
            cancelText: 'ยกเลิก',
            onConfirm: async () => {
                try {
                    const payload = {
                        id: address.id,
                        contact: { name: address.contact?.name, phone: address.contact?.phone },
                        name: address.addressLine1,
                        addressLine1: address.addressLine1,
                        addressLine2: address.addressLine2,
                        postalCode: address.postalCode,
                        country: address.country,
                        latitude: address.latitude,
                        longitude: address.longitude,
                        isDeleted: true,
                    };
                    const response = await api.UpdateAddressesAsync(user?.entityUniqueIdentifierId, payload);
                    if (response.isSuccess) {
                        alert.success({ message: 'ลบที่อยู่สำเร็จ' });
                        fetchAddresses.current();
                    } else {
                        alert.error({ message: response.message || 'ไม่สามารถลบที่อยู่ได้' });
                    }
                } catch {
                    alert.error({ message: 'เกิดข้อผิดพลาดในการลบที่อยู่' });
                }
            },
        });
    };

    const handleChange = (field: keyof typeof emptyForm) =>
        (e: React.ChangeEvent<HTMLInputElement>) =>
            setForm(prev => ({ ...prev, [field]: e.target.value }));

    if (mode === 'form') {
        return (
            <section className="am-wrapper">
                <div className="am-container">

                    <button className="am-back-btn" onClick={() => setMode('list')}>
                        ← กลับ
                    </button>

                    <div className="am-form-header">
                        <h2 className="am-form-title">แก้ไขที่อยู่</h2>
                    </div>

                    <div className="am-section-label">ข้อมูลผู้รับ</div>
                    <div className="am-card">
                        <div className="am-grid">

                            <div className="am-field">
                                <label className="am-label required">ชื่อผู้รับ</label>
                                <input type="text" placeholder="กรอกชื่อ-นามสกุล" className="am-input" value={form.name} onChange={handleChange('name')} />
                            </div>

                            <div className="am-field">
                                <label className="am-label required">เบอร์ติดต่อ</label>
                                <input type="tel" placeholder="0xx-xxx-xxxx" className="am-input" value={form.phone} onChange={handleChange('phone')} />
                            </div>

                        </div>
                    </div>

                    <div className="am-section-label">ที่อยู่</div>
                    <div className="am-card">
                        <div className="am-grid">

                            <div className="am-field full">
                                <label className="am-label required">ที่อยู่</label>
                                <input type="text" placeholder="บ้านเลขที่ ซอย ถนน แขวง เขต" className="am-input" value={form.addressLine1} onChange={handleChange('addressLine1')} />
                            </div>

                            <div className="am-field full">
                                <label className="am-label">ที่อยู่เพิ่มเติม</label>
                                <input type="text" placeholder="หมู่บ้าน อาคาร ชั้น ฯลฯ" className="am-input" value={form.addressLine2} onChange={handleChange('addressLine2')} />
                            </div>

                            <div className="am-field">
                                <label className="am-label required">รหัสไปรษณีย์</label>
                                <input type="text" placeholder="เช่น 10110" className="am-input" value={form.postalCode} onChange={handleChange('postalCode')} maxLength={5} />
                            </div>

                        </div>
                    </div>

                    <div className="am-actions">
                        <Button variant="outline" fullWidth onClick={() => setMode('list')}>ยกเลิก</Button>
                        <Button variant="primary" fullWidth onClick={handleSave} disabled={saving}>
                            {saving ? 'กำลังบันทึก...' : 'บันทึก'}
                        </Button>
                    </div>

                </div>
            </section>
        );
    }

    return (
        <section className="am-wrapper">
            <div className="am-container">

                <div className="am-list-header">
                    <h2 className="am-title">จัดการที่อยู่</h2>
                    <button className="am-add-btn" onClick={() => navigate('/location')}>+ เพิ่ม</button>
                </div>

                {addresses.length === 0 ? (
                    <div className="am-empty">
                        <span className="am-empty-text">ยังไม่มีที่อยู่ที่บันทึกไว้</span>
                        <Button variant="primary" onClick={() => navigate('/location')}>+ เพิ่มที่อยู่</Button>
                    </div>
                ) : (
                    addresses.map(address => (
                        <div key={address.id} className="am-info-card">
                            <div className="am-info-card-top">
                                <div className="am-info-card-body">
                                    <div className="am-info-card-name">{address.contact?.name}</div>
                                    <div className="am-info-card-detail">{address.contact?.phone}</div>
                                    <div className="am-info-card-detail">
                                        {address.addressLine1}
                                        {address.addressLine2 ? `, ${address.addressLine2}` : ''}
                                    </div>
                                    <div className="am-info-card-detail">รหัสไปรษณีย์ {address.postalCode}</div>
                                </div>
                                {address.isDefault && (
                                    <span className="am-default-badge">ที่อยู่หลัก</span>
                                )}
                            </div>
                            <div className="am-card-actions">
                                <button className="am-card-btn edit" onClick={() => openEdit(address)}>แก้ไข</button>
                                <button className="am-card-btn delete" onClick={() => handleDelete(address)}>ลบ</button>
                            </div>
                        </div>
                    ))
                )}

            </div>
        </section>
    );
};

export default AddressManagement;